<template>
  <Modal closable wide>
    <template v-slot:header>
      <h2>Importér adresser</h2>
    </template>

    <div>
      <div class="step" v-if="step == 0">
        <img style="width:100%;" src="/img/import_guide.gif" />
        <p>Kopier adresserne (og evt. stop-navne) fra Excel eller Google Sheets og indsæt dem i tekstfeltet herunder:</p>
        <textarea ref="pasteArea" :placeholder="platform().isWindows ? 'CTRL+V' : platform().isMac ? '⌘V' : 'Sæt ind her'" v-model="paste" @input="onPaste()" v-focus></textarea>
      </div>

      <div class="step" v-if="step == 1">
        <p>Vælg hvilken type information de enkelte kolonner indeholder. Mindst én kolonne skal indeholde en adresse eller del af en adresse (fx. postnummer).</p>
        <div class="setup-table" v-scrollable>
          <table>
              <tr>
                <th></th>
                <th v-for='(column, j) in rows[0].columns' :key="j">
                  <InputSelect :options='[{display: "Udelad", value: 0},{display: "Del af adresse", value: 1},{display: "Navn", value: 2}]' placeholder="Vælg..." v-model="colSetup[j]"></InputSelect>
                </th>
              </tr>
              <tr v-for='(row, i) in rows' :key="i">
                  <td><InputCheck v-model="row.included"/></td>
                  <td v-for='(column, j) in row.columns' :class="{'excluded': !colSetup[j] || colSetup[j] == 0 || !row.included}" :key="j">{{ column }}</td>
              </tr>
          </table>
        </div>
        <div v-if="includedRows.length > store.user.permission('LIMIT_ROUTE_PLACES')" class="color-error text-B text-c">Dit abonnement tillader kun {{store.user.permission('LIMIT_ROUTE_PLACES')}} stop per rute.</div>
      </div>

      <div class="step" v-if="step == 2">
        <div class="result-table" v-scrollable>
          <table>
              <tr>
                <th>Navn</th>
                <th>Adresse</th>
                <th style="width: 40px;"></th>
              </tr>
              <tr v-for="(row, i) in includedRows" :key="i" :class="{'color-warning': !row.loading && !row.location}">
                  <td>{{ row.name || row.location && row.location.name || '-' }}</td>
                  <td>{{ row.location && row.location.address || row.query || '-'}}</td>
                  <td>
                    <AppLoader size="small" v-if="row.loading" />
                    <AppIcon v-else
                            :name="row.location ? 'check' : 'close'"
                            :color="row.location ? 'success' : 'warning'"
                            outlined />
                  </td>
              </tr>
          </table>
        </div>
        <h3 v-if="rows.length == errorRows.length" class="color-warning text-c">Ingen stop blev ikke fundet</h3>
        <h3 v-else-if="errorRows.length != 0" class="color-warning text-c">{{ errorRows.length }} stop blev ikke fundet</h3>
        <h3 v-else class="color-success text-c">Alle stop blev fundet</h3>
      </div>


    </div>

    <template v-slot:footer>
      <template v-if="step == 1">
        <AppButton @click="reset()" style="margin-right">Tilbage</AppButton>
        <AppButton @click="search()" color="primary" :disabled="!colSetup.includes(1) || !includedRows.length || includedRows.length > store.user.permission('LIMIT_ROUTE_PLACES')">Godkend <b>{{ includedRows.length }}</b> adresser</AppButton>
      </template>
      <template v-if="step == 2">
        <AppButton v-if="rows.length == errorRows.length" @click="reset()">Start forfra</AppButton>
        <AppButton v-else @click="save()" color="primary" :loading="saving" :disabled="!isCompleted">Tilføj <b>{{ includedRows.length - errorRows.length }}</b> fundne adresser</AppButton>
      </template>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'

import { store } from '@/store'
import InputSelect from '@/components/inputs/InputSelect.vue'
import InputCheck from '@/components/inputs/InputCheck.vue'
import { searchLocations } from '@/services/map.service'
import { platform } from '@/api/tools'

import { Place, PlaceModel, Location } from '@/api/tripply/models'
import { GA } from '@/api/analytics'

interface ImportRow {
  columns: string[];
  query: string;
  name: string;
  included: boolean;
  loading: boolean;
  location: Location | null;
}

export default defineComponent({
  name: 'ImportPlaces',
  props: {
    routeId: {
      type: String,
      required: true
    }
  },
  components: { InputSelect, InputCheck },
  setup(props, { emit }) {

    const step = ref(0)
    const paste = ref('')
    const rows = ref<ImportRow[]>([])
    const colSetup = ref([])
    const isCompleted = ref(false)

    const includedRows = computed(() => rows.value.filter(row => row.included))
    const errorRows = computed(() => rows.value.filter(row => row.included && row.location == null))

    const places: Place[] = []

    const saving = ref(false)

    function onPaste() {
      rows.value = []
      paste.value.split('\n').forEach((row, i) => {
        if (row) {
          rows.value.push({
            columns: row.split('\t'),
            query: '',
            name: '',
            included: true,
            loading: true,
            location: null
          })
        }
      })
      step.value = 1
    }

    async function search () {
      step.value = 2
      rows.value.forEach(async row => {
        if (row.included) {
          row.columns.forEach((column, j) => {
            
            if (colSetup.value[j] == 1) { // If part of address
              row.query += column + ' '
            }
            if (colSetup.value[j] == 2) { // If name
              row.name += column + ' '
            }
          })
          const result = await searchLocations(row.query, 1)
          row.loading = false
          if (result.length) {
            row.location = result[0]
            if (row.name) row.location.name = row.name
            places.push(new PlaceModel(row.location))
          }
        }
      })    
      isCompleted.value = true  
    }

    async function save() {
      saving.value = true
      store.route.addPlaces(props.routeId, places)
      GA('add_place', { method: 'import', number: places.length })
      saving.value = false
      store.modal.hideModal('PlaceImporter')
    }

    function reset() {
      step.value = 0
      paste.value = ''
      colSetup.value = []
    }

    return { store, step, paste, onPaste, rows, colSetup, search, save, saving, isCompleted, errorRows, platform, includedRows, reset }
  }
})
</script>

<style lang="scss" scoped>

textarea {
  width: 100%;
  height: 4rem;
  resize: none;
  padding: 1rem;
  font-size: 1.5rem;
  text-align: center;
  border: 1px dashed grey;
  overflow: hidden;
  background: var(--color-background-input);
}

.setup-table, .result-table {
  max-height: 14rem;
  min-height: 11rem;
  overflow: auto;
  margin-bottom: 2rem;
  th {
    z-index: 100;
    width: auto;
    position: sticky;
    background: var(--color-card);
    top: 0;
  }
}

.setup-table {
  table {
    table-layout: fixed;
    th {
      padding: .5rem .25rem;
      &:first-child {
        width: 40px;
      }
    }
    td {
      &.excluded {
        background: var(--color-divider);
        opacity: 0.75;
        text-decoration: line-through;
      }
    }
  }
}

.result-table {
  table {
    th {
      &:last-child {
        width: 40px;
      }
    }
    td {
      &.excluded {
        background: var(--color-divider);
        opacity: 0.75;
        text-decoration: line-through;
      }
    }
  }
}

</style>